import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFlagsmithLoading } from '@flagsmith/flagsmith/react';

import type { EMessageChannel } from 'src/services/api';
import { EFeatureFlag, useIsFeatureEnabled } from 'src/utils/feature-flags';
import { Config } from 'src/config';
import { getConversationPath } from 'src/pages/landlord/leads/messages/context/mode/get-conversation-path';
import { usePersistentState } from 'src/services/storage/use-persistent-state';

/** Minimum viewport width (px) at which the conversation drawer replaces full-page navigation. */
export const CONVERSATION_DRAWER_MIN_WIDTH = 1280;

/** Persistent-storage key backing the user's drawer on/off preference (see `useIsConversationDrawerSettingEnabled`). */
export const CONVERSATION_DRAWER_SETTING_KEY = 'conversation-drawer-enabled';

const isBooleanValue = (value: unknown): value is boolean => typeof value === 'boolean';

/**
 * The user-facing "open conversations in a side panel" preference (Account settings). Defaults to
 * `true` so the `CONVERSATION_DRAWER` feature flag remains the thing that controls exposure to the
 * feature; this setting is a per-user opt-out on top of it. Backed by IndexedDB via
 * `usePersistentState`, so `isReady` starts `false` and the value starts at the default until the
 * stored value (if any) loads — see `useConversationDrawerFallbackRedirect` for how that race is
 * handled for deep links.
 */
export function useIsConversationDrawerSettingEnabled(): { isEnabled: boolean; isReady: boolean; setEnabled: (value: boolean) => Promise<void> } {
    const [isEnabled, setEnabled, , isReady] = usePersistentState<boolean>(CONVERSATION_DRAWER_SETTING_KEY, true, isBooleanValue);
    return { isEnabled, isReady, setEnabled };
}

/**
 * Whether the drawer is *available* on this surface — the `CONVERSATION_DRAWER` flag is on and the
 * viewport is wide enough — regardless of the user's own on/off preference. Used to decide whether
 * to render the layout toggle at all: below `CONVERSATION_DRAWER_MIN_WIDTH` (or with the flag off)
 * the preference has no effect, so the control would be meaningless.
 */
export function useIsConversationDrawerSupported(): boolean {
    const isFlagEnabled = useIsFeatureEnabled(EFeatureFlag.CONVERSATION_DRAWER);
    const isViewportWideEnough = useMediaQuery(`(min-width:${CONVERSATION_DRAWER_MIN_WIDTH}px)`);
    return isFlagEnabled && isViewportWideEnough;
}

/**
 * Whether the conversation drawer should be used instead of full-page navigation: the
 * `CONVERSATION_DRAWER` feature flag is on AND the viewport is at least
 * `CONVERSATION_DRAWER_MIN_WIDTH` AND the user hasn't opted out via the Account settings toggle.
 * When false, callers keep the current full-page navigation.
 */
export function useIsConversationDrawerEnabled(): boolean {
    const isSupported = useIsConversationDrawerSupported();
    const { isEnabled: isSettingEnabled, isReady: isSettingReady } = useIsConversationDrawerSettingEnabled();

    // Require the persisted setting to have loaded before treating it as "on": until then, the
    // permissive default would otherwise let the drawer open for a beat even when the user has
    // actually turned it off (use-open-conversation.ts consumes this same gate to decide whether
    // to open the drawer or navigate full-page) — see useConversationDrawerFallbackRedirect for
    // why this doesn't cause a premature redirect while loading.
    return isSupported && isSettingReady && isSettingEnabled;
}

/**
 * Drives the conversation drawer's open state from the current route's search params, so the
 * inbox/search page keeps its own `tab`/`filter`/`s-search` params intact and the drawer stays
 * deep-linkable (browser Back closes it).
 */
export function useConversationDrawer() {
    const isEnabled = useIsConversationDrawerEnabled();
    const [searchParams, setSearchParams] = useSearchParams();

    const leadUuid = searchParams.get('lead_uuid');

    const open = useCallback((newLeadUuid: string, channel: EMessageChannel, showNavigation: boolean = true) => {
        setSearchParams((prev) => {
            const newSearchParams = new URLSearchParams(prev);
            newSearchParams.set('lead_uuid', newLeadUuid);
            newSearchParams.set('channel', channel);
            if (showNavigation) {
                newSearchParams.set('show_navigation', 'true');
            } else {
                newSearchParams.delete('show_navigation');
            }
            return newSearchParams;
        });
    }, [setSearchParams]);

    const close = useCallback(() => {
        setSearchParams((prev) => {
            const newSearchParams = new URLSearchParams(prev);
            newSearchParams.delete('lead_uuid');
            newSearchParams.delete('channel');
            newSearchParams.delete('show_navigation');
            newSearchParams.delete('sidebarTab');
            return newSearchParams;
        });
    }, [setSearchParams]);

    return {
        isEnabled,
        leadUuid,
        open,
        close,
    };
}

/**
 * Deep-link / viewport-shrink fallback: if `lead_uuid` is in the URL but the drawer is genuinely
 * unavailable (flag off, or viewport too narrow — including a live resize while the drawer was
 * open), nothing renders that conversation and the params would otherwise linger. This redirects
 * (replace, so it doesn't pile up history) to the equivalent full-page conversation URL.
 *
 * Does NOT redirect while the CONVERSATION_DRAWER Flagsmith flag or the persisted drawer setting
 * are still resolving (only "confirmed off" triggers it, not "we don't know yet") — see
 * isFlagStateKnown / isSettingStateKnown below. A narrow viewport alone is sufficient grounds to
 * redirect regardless of flag/setting state, since viewport width has no such loading race.
 */
export function useConversationDrawerFallbackRedirect(): void {
    const isEnabled = useIsConversationDrawerEnabled();
    const isViewportWideEnough = useMediaQuery(`(min-width:${CONVERSATION_DRAWER_MIN_WIDTH}px)`);
    const flagsmithLoading = useFlagsmithLoading();
    const { isReady: isSettingReady } = useIsConversationDrawerSettingEnabled();
    const isFlagStateKnown = Config.isLocal || flagsmithLoading?.isLoading === false;
    const isSettingStateKnown = isSettingReady;
    const isKnownUnavailable = !isEnabled && (!isViewportWideEnough || (isFlagStateKnown && isSettingStateKnown));

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const redirectedLeadUuidRef = useRef<string | null>(null);

    const leadUuid = searchParams.get('lead_uuid');
    const channel = searchParams.get('channel');
    const showNavigationParam = searchParams.get('show_navigation');

    useEffect(() => {
        if (!leadUuid || !isKnownUnavailable) {
            return;
        }
        if (redirectedLeadUuidRef.current === leadUuid) {
            return;
        }

        const backSearchParams = new URLSearchParams(searchParams);
        backSearchParams.delete('lead_uuid');
        backSearchParams.delete('channel');
        backSearchParams.delete('show_navigation');
        backSearchParams.delete('sidebarTab');
        const backQuery = backSearchParams.toString();
        const backPath = `${location.pathname}${backQuery ? `?${backQuery}` : ''}`;

        // `channel` is intentionally optional here: LeadMessagesSurface/Provider derives a
        // default channel from the lead when none is present, so redirecting on `leadUuid` alone
        // (rather than dead-ending when `channel` is missing) is safe.
        const path = getConversationPath((channel as EMessageChannel) || undefined, leadUuid, backPath, showNavigationParam === 'true');
        if (path) {
            // Only mark this leadUuid as handled once a redirect actually fires, so a dead-end
            // (which can no longer happen now that `channel` is optional, but keep this safe)
            // doesn't permanently suppress a later retry.
            redirectedLeadUuidRef.current = leadUuid;
            navigate(path, { replace: true });
        }
    }, [leadUuid, isKnownUnavailable, channel, showNavigationParam, searchParams, location.pathname, navigate]);
}
